import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SumsubConfig {
  levelName?: string;
  lang?: string;
  email?: string;
  phone?: string;
  /** Optional stable external user ID. When provided, overrides the auto-generated
   *  per-session ID so the token is tied to a specific person (e.g. from /verify link). */
  userId?: string;
  onComplete?: (payload: SumsubCompletionPayload) => void;
  onError?: (error: SumsubError) => void;
  onStepCompleted?: (step: string, payload: unknown) => void;
}

export interface SumsubCompletionPayload {
  applicantId: string;
  reviewStatus: 'pending' | 'completed' | 'init';
  reviewResult?: {
    reviewAnswer: 'GREEN' | 'RED' | 'YELLOW';
    rejectLabels?: string[];
    reviewRejectType?: 'FINAL' | 'RETRY';
  };
}

export interface SumsubError {
  code: string;
  message: string;
}

interface SumsubState {
  isLoading: boolean;
  isReady: boolean;
  error: string | null;
  accessToken: string | null;
}

export type { SumsubConfig };

/**
 * Real Sumsub hook using the WebSDK
 */
export function useSumsub(config: SumsubConfig = {}) {
  const [state, setState] = useState<SumsubState>({
    isLoading: false,
    isReady: false,
    error: null,
    accessToken: null
  });

  const configRef = useRef(config);
  configRef.current = config;
  const sdkInstanceRef = useRef<any>(null);

  // Stable externalUserId — MUST be the same across initial token + all refreshes.
  // If a userId is provided via config (e.g. personId from /verify link), use that.
  // Otherwise generate a unique per-session ID to avoid Sumsub "Smart Reuse" screen.
  // IMPORTANT: The ref is seeded once. If config.userId is provided, always use it —
  // it is already stable (stored in sessionStorage by the parent component).
  const externalUserIdRef = useRef<string>(
    config.userId || `onboarding-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  );
  // If the parent provides a userId (from sessionStorage), sync it into the ref immediately.
  // This handles cases where the hook mounts before config.userId is available.
  if (config.userId && externalUserIdRef.current !== config.userId) {
    externalUserIdRef.current = config.userId;
  }

  const fetchAccessToken = useCallback(async (): Promise<string> => {
    const userId = externalUserIdRef.current;
    
    console.log('Fetching Sumsub access token for:', userId);
    
    const { data, error } = await supabase.functions.invoke('sumsub-token', {
      body: { 
        userId,
        levelName: configRef.current.levelName,
        email: configRef.current.email,
        phone: configRef.current.phone
      }
    });
    
    if (error) {
      console.error('Error fetching access token:', error);
      throw new Error(error.message || 'Failed to fetch access token');
    }
    
    if (!data?.accessToken) {
      throw new Error('No access token received');
    }
    
    console.log('Access token received successfully for user:', userId);
    return data.accessToken;
  }, []);

  const launch = useCallback(async (containerSelector: string) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      // Dynamically import the Sumsub SDK
      const snsWebSdk = (await import('@sumsub/websdk')).default;
      
      // Fetch initial access token
      const accessToken = await fetchAccessToken();
      
      setState(prev => ({ ...prev, accessToken }));

      // Build the SDK instance
      // CRITICAL: The token refresh callback MUST return a Promise<string>.
      // It reuses the same externalUserId via externalUserIdRef to prevent
      // Sumsub from creating a duplicate applicant on refresh.
      const snsWebSdkInstance = snsWebSdk
        .init(accessToken, () => fetchAccessToken())
        .withConf({
          lang: configRef.current.lang || 'en',
        })
        .on('idCheck.onStepCompleted', (payload: any) => {
          console.log('Sumsub step completed:', payload);
          configRef.current.onStepCompleted?.(payload.idDocSetType || payload.step, payload);
        })
        .on('idCheck.onApplicantStatusChanged', (payload: any) => {
          console.log('Sumsub applicant status changed:', payload);
          const answer = payload.reviewResult?.reviewAnswer;
          const rejectType = payload.reviewResult?.reviewRejectType;
          
          // GREEN/YELLOW = approved, trigger completion
          if (answer === 'GREEN' || answer === 'YELLOW') {
            configRef.current.onComplete?.({
              applicantId: payload.applicantId || 'unknown',
              reviewStatus: 'completed',
              reviewResult: payload.reviewResult
            });
          }
          // RED + FINAL = permanent rejection, trigger completion with failure
          else if (answer === 'RED' && rejectType === 'FINAL') {
            configRef.current.onComplete?.({
              applicantId: payload.applicantId || 'unknown',
              reviewStatus: 'completed',
              reviewResult: payload.reviewResult
            });
          }
          // RED + RETRY = user can retry in SDK, do NOT trigger completion
          // The SDK will show its own retry UI
        })
        .on('idCheck.applicantStatus', (payload: any) => {
          console.log('Sumsub applicant final status:', payload);
          const answer = payload.reviewResult?.reviewAnswer;
          const rejectType = payload.reviewResult?.reviewRejectType;
          
          if (answer === 'GREEN' || answer === 'YELLOW') {
            configRef.current.onComplete?.({
              applicantId: payload.applicantId || 'unknown',
              reviewStatus: payload.reviewStatus || 'completed',
              reviewResult: payload.reviewResult
            });
          } else if (answer === 'RED' && rejectType === 'FINAL') {
            configRef.current.onComplete?.({
              applicantId: payload.applicantId || 'unknown',
              reviewStatus: payload.reviewStatus || 'completed',
              reviewResult: payload.reviewResult
            });
          }
          // RED + RETRY: let the SDK handle it
        })
        .on('idCheck.onApplicantSubmitted', (payload: any) => {
          console.log('Sumsub applicant submitted (all steps done):', payload);
          // Do NOT trigger onComplete here — this fires after each step group
          // (e.g. after liveness but before POA). Wait for the final review event.
        })
        .on('idCheck.onApplicantReviewComplete', (payload: any) => {
          console.log('Sumsub review complete:', payload);
          configRef.current.onComplete?.({
            applicantId: payload.applicantId || 'unknown',
            reviewStatus: 'completed',
            reviewResult: payload.reviewResult
          });
        })
        .on('idCheck.onError', (error: any) => {
          console.error('Sumsub error:', error);
          configRef.current.onError?.({ 
            code: error.code || 'UNKNOWN', 
            message: error.message || 'Verification error' 
          });
        })
        .on('idCheck.moduleResultPresented', (payload: any) => {
          console.log('Sumsub module result presented:', payload);
          // Do NOT trigger onComplete here — this fires per module (ID, liveness, POA)
          // and would prematurely end the flow before all modules are done.
        })
        .on('idCheck.onActionCompleted', (payload: any) => {
          console.log('Sumsub action completed:', payload);
        })
        .build();

      // Store reference for cleanup
      sdkInstanceRef.current = snsWebSdkInstance;

      // Launch the SDK
      snsWebSdkInstance.launch(containerSelector);
      
      setState(prev => ({ ...prev, isLoading: false, isReady: true }));
      
    } catch (error) {
      console.error('Failed to launch Sumsub:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize verification';
      setState(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: errorMessage 
      }));
      configRef.current.onError?.({ code: 'INIT_ERROR', message: errorMessage });
    }
  }, [fetchAccessToken]);

  const destroy = useCallback(() => {
    if (sdkInstanceRef.current) {
      try {
        sdkInstanceRef.current.destroy();
      } catch (e) {
        console.warn('Error destroying Sumsub SDK:', e);
      }
      sdkInstanceRef.current = null;
    }
  }, []);

  return {
    launch,
    destroy,
    isLoading: state.isLoading,
    isReady: state.isReady,
    error: state.error,
    accessToken: state.accessToken,
    externalUserId: externalUserIdRef.current,
    isDemo: false
  };
}

/**
 * Demo/mock version of the hook for testing without Sumsub credentials
 */
export function useSumsubDemo(config: SumsubConfig = {}) {
  const [state, setState] = useState<SumsubState>({
    isLoading: false,
    isReady: false,
    error: null,
    accessToken: null
  });

  const configRef = useRef(config);
  configRef.current = config;

  const launch = useCallback(async (_containerSelector: string) => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    // Simulate loading time
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setState(prev => ({ ...prev, isLoading: false, isReady: true }));
    
    // Simulate verification completion after delay
    setTimeout(() => {
      configRef.current.onComplete?.({
        applicantId: 'demo-applicant-id',
        reviewStatus: 'completed',
        reviewResult: {
          reviewAnswer: 'GREEN'
        }
      });
    }, 3000);
  }, []);

  return {
    launch,
    destroy: () => {},
    isLoading: state.isLoading,
    isReady: state.isReady,
    error: state.error,
    accessToken: 'demo-token',
    isDemo: true
  };
}
